import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, formatINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Cpu, LogOut, Plus, Pencil, Trash2, Package, ClipboardList, TrendingUp, IndianRupee, Upload, Gift, MessageCircle, ShoppingBag, Wrench } from "lucide-react";
import { TechValueLockup } from "@/components/HeaderNav";
import { API } from "@/lib/api";

const STATUS_STYLES = {
  pending: "bg-amber-500/15 text-amber-600 border-amber-500/30",
  inspecting: "bg-primary/15 text-primary border-primary/30",
  completed: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
  cancelled: "bg-destructive/15 text-destructive border-destructive/30",
};

const SLOT_LABELS = {
  morning: "Morning (10 AM – 1 PM)",
  afternoon: "Afternoon (1 PM – 5 PM)",
  evening: "Evening (5 PM – 9 PM)",
};

const STATUS_MESSAGES = {
  pending: (o) => `Hi ${o.customer_name}! 👋\n\nYour Tech Value pickup is *confirmed*.\n\nRef: ${o.ref}\nDevice: ${o.product_name} · ${o.storage}\nPayout: ₹${o.final_price.toLocaleString("en-IN")}\nPickup: ${o.pickup_date} · ${SLOT_LABELS[o.pickup_slot] || o.pickup_slot}\n\nOur agent will visit your doorstep. Track anytime: ${window.location.origin}/track\n\n— Tech Value (Tech World, Vashi)`,
  inspecting: (o) => `Hi ${o.customer_name}! 👋\n\nOur agent is *on the way / inspecting your device* for order ${o.ref}.\n\nDevice: ${o.product_name} · ${o.storage}\nAgreed Payout: ₹${o.final_price.toLocaleString("en-IN")}\n\nKeep your ID handy for verification. Live status: ${window.location.origin}/track\n\n— Tech Value`,
  completed: (o) => `Hi ${o.customer_name}! 🎉\n\nPayment *completed* for order ${o.ref}.\n\nDevice: ${o.product_name} · ${o.storage}\nPaid: ₹${o.final_price.toLocaleString("en-IN")} via Cash / UPI\n\nThanks for choosing Tech Value! Share your referral code with friends to earn ₹200 per pickup.\n\n— Tech Value`,
  cancelled: (o) => `Hi ${o.customer_name},\n\nYour order ${o.ref} has been *cancelled*.\n\nIf this was a mistake, WhatsApp us at +91 81048 78405 for a fresh quote.\n\n— Tech Value`,
};

function buildWhatsAppNotifyLink(order) {
  // Normalize phone → prepend country code if user entered 10 digits
  let phone = (order.customer_phone || "").replace(/\D/g, "");
  if (phone.length === 10) phone = "91" + phone;
  const msg = (STATUS_MESSAGES[order.status] || STATUS_MESSAGES.pending)(order);
  return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
}

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [resale, setResale] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [editing, setEditing] = useState(null); // product being edited/created
  const [editingResale, setEditingResale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [csvImporting, setCsvImporting] = useState(false);
  const csvInputRef = React.useRef(null);

  const handleCsvImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("tv_admin_token");
      const res = await fetch(`${API}/admin/products/import-csv?mode=merge`, {
        method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Import failed (${res.status})`);
      }
      const data = await res.json();
      toast.success(`CSV imported · ${data.inserted} new, ${data.updated} updated`);
      if (data.errors && data.errors.length > 0) {
        toast.warning(`${data.errors.length} row(s) skipped — check format`);
      }
      loadAll();
    } catch (err) {
      toast.error(err.message || "CSV import failed");
    } finally {
      setCsvImporting(false);
      if (csvInputRef.current) csvInputRef.current.value = "";
    }
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, p, o, r, rs, rp] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/products?active_only=false"),
        api.get("/admin/orders"),
        api.get("/admin/referrals"),
        api.get("/resale?active_only=false"),
        api.get("/admin/repairs"),
      ]);
      setStats(s.data);
      setProducts(p.data);
      setOrders(o.data);
      setReferrals(r.data);
      setResale(rs.data);
      setRepairs(rp.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}/status`, { status });
      toast.success("Order updated");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const updateReferralStatus = async (id, status) => {
    try {
      await api.patch(`/admin/referrals/${id}/status`, { status });
      toast.success("Referral updated");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const updateRepair = async (id, updates) => {
    try {
      await api.patch(`/admin/repairs/${id}`, updates);
      toast.success("Repair updated");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Product deleted");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteResale = async (id) => {
    if (!window.confirm("Delete this stock item?")) return;
    try {
      await api.delete(`/resale/${id}`);
      toast.success("Stock item deleted");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="border-b border-border tv-glass sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <TechValueLockup size="md" tagline={false} />
          <div className="flex items-center gap-3">
            <div className="text-sm text-right hidden sm:block">
              <div className="font-medium">{admin?.name}</div>
              <div className="text-xs text-muted-foreground">{admin?.email}</div>
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Stats */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard Icon={ClipboardList} label="Total Orders" value={stats?.total_orders ?? "—"} />
          <StatCard Icon={Package} label="Pending Pickups" value={stats?.pending_orders ?? "—"} accent="amber" />
          <StatCard Icon={TrendingUp} label="Completed" value={stats?.completed_orders ?? "—"} accent="emerald" />
          <StatCard Icon={IndianRupee} label="Total Paid Out" value={formatINR(stats?.total_paid ?? 0)} accent="emerald" />
        </div>

        <Tabs defaultValue="orders">
          <TabsList className="mb-4">
            <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products">Buyback Catalog</TabsTrigger>
            <TabsTrigger value="resale" data-testid="tab-resale">
              <ShoppingBag className="w-3.5 h-3.5 mr-1.5" /> Available Stock
              {resale.filter((r) => r.active && !r.sold).length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-mono">
                  {resale.filter((r) => r.active && !r.sold).length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="referrals" data-testid="tab-referrals">
              Referrals
              {referrals.filter((r) => r.status === "pending").length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-mono">
                  {referrals.filter((r) => r.status === "pending").length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="repairs" data-testid="tab-repairs">
              <Wrench className="w-3.5 h-3.5 mr-1.5" /> Repairs
              {repairs.filter((r) => r.status === "new").length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 rounded-full bg-primary text-white text-[10px] font-mono">
                  {repairs.filter((r) => r.status === "new").length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="orders">
            <Card>
              <CardHeader><CardTitle>Live Orders</CardTitle></CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/60 text-xs uppercase tracking-widest">
                    <tr>
                      <th className="text-left p-3">Ref</th>
                      <th className="text-left p-3">Device</th>
                      <th className="text-left p-3">Customer</th>
                      <th className="text-left p-3">Pickup</th>
                      <th className="text-right p-3">Price</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-right p-3">Notify</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Loading…</td></tr>}
                    {!loading && orders.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No orders yet.</td></tr>}
                    {orders.map((o) => (
                      <tr key={o.id} className="border-t border-border" data-testid={`order-row-${o.ref}`}>
                        <td className="p-3 font-mono font-semibold">{o.ref}</td>
                        <td className="p-3">
                          <div className="font-medium">{o.product_name}</div>
                          <div className="text-xs text-muted-foreground">{o.storage}</div>
                        </td>
                        <td className="p-3">
                          <div className="font-medium">{o.customer_name}</div>
                          <div className="text-xs text-muted-foreground">{o.customer_phone}</div>
                        </td>
                        <td className="p-3 text-xs">
                          <div>{o.pickup_date}</div>
                          <div className="text-muted-foreground capitalize">{o.pickup_slot}</div>
                        </td>
                        <td className="p-3 text-right font-mono font-bold text-accent">{formatINR(o.final_price)}</td>
                        <td className="p-3">
                          <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                            <SelectTrigger className="w-36 h-8" data-testid={`order-status-select-${o.ref}`}>
                              <Badge variant="outline" className={STATUS_STYLES[o.status]}>{o.status}</Badge>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="inspecting">Inspecting</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                              <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-3 text-right">
                          <a
                            href={buildWhatsAppNotifyLink(o)}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`notify-whatsapp-${o.ref}`}
                            title="Notify customer on WhatsApp"
                          >
                            <Button size="sm" className="bg-[#25D366] hover:bg-[#20BD5A] text-white h-8 gap-1.5">
                              <MessageCircle className="w-3.5 h-3.5" />
                              <span className="hidden sm:inline">Notify</span>
                            </Button>
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="products">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
                <CardTitle>Buyback Catalog</CardTitle>
                <div className="flex gap-2 flex-wrap">
                  <input
                    ref={csvInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleCsvImport}
                    className="hidden"
                    data-testid="csv-import-input"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      const token = localStorage.getItem("tv_admin_token");
                      const res = await fetch(`${API}/admin/products/csv-template`, {
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url; a.download = "tech-world-price-template.csv"; a.click();
                      URL.revokeObjectURL(url);
                    }}
                    data-testid="download-csv-template-btn"
                  >
                    <Upload className="w-3.5 h-3.5 mr-1.5 rotate-180" /> Download Template
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => csvInputRef.current?.click()}
                    disabled={csvImporting}
                    data-testid="import-csv-btn"
                  >
                    <Upload className="w-3.5 h-3.5 mr-1.5" />
                    {csvImporting ? "Importing…" : "Import CSV"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      if (!window.confirm("This will WIPE all current products and reload them from the Tech World price guide. Continue?")) return;
                      try {
                        const { data } = await api.post("/admin/products/reseed");
                        toast.success(`Catalog reset · ${data.count} products loaded`);
                        loadAll();
                      } catch (e) {
                        toast.error(formatApiError(e));
                      }
                    }}
                    data-testid="reseed-products-btn"
                  >
                    <Upload className="w-3.5 h-3.5 mr-1.5" /> Reset to Guide
                  </Button>
                  <Button size="sm" onClick={() => setEditing({ new: true, brand: "apple", name: "", image_url: "", storage_variants: [{ label: "128 GB", base_price: 40000 }], active: true })} data-testid="add-product-btn">
                    <Plus className="w-4 h-4 mr-1.5" /> Add Product
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/60 text-xs uppercase tracking-widest">
                    <tr>
                      <th className="text-left p-3">Model</th>
                      <th className="text-left p-3">Brand</th>
                      <th className="text-left p-3">Variants</th>
                      <th className="text-right p-3">Max Price</th>
                      <th className="text-left p-3">Active</th>
                      <th className="text-right p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((p) => (
                      <tr key={p.id} className="border-t border-border" data-testid={`product-row-${p.slug}`}>
                        <td className="p-3 font-medium">{p.name}</td>
                        <td className="p-3 capitalize">{p.brand}</td>
                        <td className="p-3 text-xs text-muted-foreground">{p.storage_variants.map((v) => v.label).join(", ")}</td>
                        <td className="p-3 text-right font-mono font-semibold text-accent">{formatINR(Math.max(...p.storage_variants.map((v) => v.base_price)))}</td>
                        <td className="p-3">
                          <Badge variant="outline" className={p.active ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30" : "bg-muted"}>
                            {p.active ? "Active" : "Hidden"}
                          </Badge>
                        </td>
                        <td className="p-3 text-right">
                          <Button variant="ghost" size="sm" onClick={() => setEditing({ ...p })} data-testid={`edit-product-${p.slug}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteProduct(p.id)} className="text-destructive" data-testid={`delete-product-${p.slug}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="resale">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Available Stock (Resale)</CardTitle>
                <Button onClick={() => setEditingResale({ new: true, brand: "apple", name: "", storage: "", condition: "excellent", price: 0, original_price: 0, image_url: "", description: "", warranty_months: 3, active: true, sold: false })} data-testid="add-resale-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> Add Stock
                </Button>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/60 text-xs uppercase tracking-widest">
                    <tr>
                      <th className="text-left p-3">Image</th>
                      <th className="text-left p-3">Model</th>
                      <th className="text-left p-3">Condition</th>
                      <th className="text-right p-3">Price</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-right p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resale.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">
                      <ShoppingBag className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      No stock added yet. Click &quot;Add Stock&quot; to list a phone for resale.
                    </td></tr>}
                    {resale.map((p) => (
                      <tr key={p.id} className="border-t border-border" data-testid={`resale-row-${p.id}`}>
                        <td className="p-3">
                          {p.image_url ? <img src={p.image_url} alt="" className="w-12 h-12 rounded-lg object-cover border border-border" /> : <div className="w-12 h-12 rounded-lg bg-secondary" />}
                        </td>
                        <td className="p-3">
                          <div className="font-medium">{p.name}</div>
                          <div className="text-xs text-muted-foreground">{p.storage || "—"} · {p.warranty_months}mo warranty</div>
                        </td>
                        <td className="p-3 capitalize">{p.condition.replace("-", " ")}</td>
                        <td className="p-3 text-right font-mono font-bold text-accent">
                          {formatINR(p.price)}
                          {p.original_price > p.price && (
                            <div className="text-xs text-muted-foreground line-through">{formatINR(p.original_price)}</div>
                          )}
                        </td>
                        <td className="p-3">
                          <Badge variant="outline" className={
                            p.sold ? "bg-slate-500/15 text-slate-600 border-slate-500/30" :
                            p.active ? "bg-emerald-500/15 text-emerald-600 border-emerald-500/30" :
                            "bg-muted"
                          }>
                            {p.sold ? "Sold" : p.active ? "Live" : "Hidden"}
                          </Badge>
                        </td>
                        <td className="p-3 text-right">
                          <Button variant="ghost" size="sm" onClick={() => setEditingResale({ ...p })} data-testid={`edit-resale-${p.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteResale(p.id)} className="text-destructive" data-testid={`delete-resale-${p.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="referrals">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Referral Rewards</CardTitle>
                <div className="text-sm text-muted-foreground font-mono">
                  Pending: <span className="text-amber-600 font-bold">{formatINR(referrals.filter((r) => r.status === "pending").reduce((s, r) => s + r.amount, 0))}</span> ·
                  Paid: <span className="text-emerald-600 font-bold">{formatINR(referrals.filter((r) => r.status === "paid").reduce((s, r) => s + r.amount, 0))}</span>
                </div>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/60 text-xs uppercase tracking-widest">
                    <tr>
                      <th className="text-left p-3">Referrer</th>
                      <th className="text-left p-3">Code</th>
                      <th className="text-left p-3">From Order</th>
                      <th className="text-right p-3">Amount</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-right p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referrals.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No referrals yet.</td></tr>}
                    {referrals.map((r) => (
                      <tr key={r.id} className="border-t border-border" data-testid={`referral-row-${r.id}`}>
                        <td className="p-3">
                          <div className="font-medium">{r.referrer_name}</div>
                          <div className="text-xs text-muted-foreground">{r.referrer_phone}</div>
                        </td>
                        <td className="p-3 font-mono font-semibold">{r.referrer_code}</td>
                        <td className="p-3">
                          <div className="font-mono font-semibold">{r.referred_order_ref}</div>
                          <div className="text-xs text-muted-foreground">{r.referred_customer_name}</div>
                        </td>
                        <td className="p-3 text-right font-mono font-bold text-accent">{formatINR(r.amount)}</td>
                        <td className="p-3">
                          <Badge variant="outline" className={
                            r.status === "paid" ? "bg-emerald-500/15 text-emerald-600 border-emerald-500/30" :
                            r.status === "cancelled" ? "bg-destructive/15 text-destructive border-destructive/30" :
                            "bg-amber-500/15 text-amber-600 border-amber-500/30"
                          }>{r.status}</Badge>
                        </td>
                        <td className="p-3 text-right space-x-2">
                          {r.status === "pending" && (
                            <>
                              <Button size="sm" onClick={() => updateReferralStatus(r.id, "paid")} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`pay-referral-${r.id}`}>
                                Mark Paid
                              </Button>
                              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => updateReferralStatus(r.id, "cancelled")} data-testid={`cancel-referral-${r.id}`}>
                                Cancel
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="repairs">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Repair Leads</CardTitle>
                <div className="text-sm text-muted-foreground font-mono">
                  New: <span className="text-primary font-bold">{repairs.filter((r) => r.status === "new").length}</span> ·
                  In-repair: <span className="text-amber-600 font-bold">{repairs.filter((r) => r.status === "in-repair").length}</span> ·
                  Delivered: <span className="text-emerald-600 font-bold">{repairs.filter((r) => r.status === "delivered").length}</span>
                </div>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/60 text-xs uppercase tracking-widest">
                    <tr>
                      <th className="text-left p-3">Ref</th>
                      <th className="text-left p-3">Device</th>
                      <th className="text-left p-3">Issue</th>
                      <th className="text-left p-3">Customer</th>
                      <th className="text-left p-3">Service</th>
                      <th className="text-right p-3">Quote</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-right p-3">Notify</th>
                    </tr>
                  </thead>
                  <tbody>
                    {repairs.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">
                      <Wrench className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      No repair requests yet.
                    </td></tr>}
                    {repairs.map((r) => {
                      let phone = (r.customer_phone || "").replace(/\D/g, "");
                      if (phone.length === 10) phone = "91" + phone;
                      const waMsg = encodeURIComponent(
                        `Hi ${r.customer_name}! 👋\n\nRegarding your Tech World repair request (${r.ref})\nDevice: ${r.brand} ${r.model}\nIssue: ${r.issue}\n\nEstimated cost: ₹${r.quoted_price || "___"}\nApproximate time: ___\n\nShall we proceed?\n\n— Tech World, Vashi`
                      );
                      return (
                        <tr key={r.id} className="border-t border-border" data-testid={`repair-row-${r.ref}`}>
                          <td className="p-3 font-mono font-semibold">{r.ref}</td>
                          <td className="p-3">
                            <div className="font-medium">{r.brand} {r.model}</div>
                            <div className="text-xs text-muted-foreground capitalize">{r.device_type}</div>
                          </td>
                          <td className="p-3 text-xs max-w-[220px]">
                            <div className="font-medium truncate">{r.issue}</div>
                            {r.notes && <div className="text-muted-foreground truncate">{r.notes}</div>}
                          </td>
                          <td className="p-3">
                            <div className="font-medium">{r.customer_name}</div>
                            <div className="text-xs text-muted-foreground">{r.customer_phone}</div>
                          </td>
                          <td className="p-3 text-xs">
                            <div className="capitalize">{r.service_mode.replace("-", " ")}</div>
                            {r.preferred_date && <div className="text-muted-foreground">{r.preferred_date}</div>}
                          </td>
                          <td className="p-3 text-right">
                            <Input
                              type="number"
                              value={r.quoted_price || ""}
                              onChange={(e) => updateRepair(r.id, { quoted_price: Number(e.target.value) })}
                              placeholder="—"
                              className="w-24 h-8 text-right font-mono"
                              data-testid={`repair-quote-${r.ref}`}
                            />
                          </td>
                          <td className="p-3">
                            <Select value={r.status} onValueChange={(v) => updateRepair(r.id, { status: v })}>
                              <SelectTrigger className="w-32 h-8" data-testid={`repair-status-${r.ref}`}>
                                <Badge variant="outline" className={
                                  r.status === "new" ? "bg-primary/15 text-primary border-primary/30" :
                                  r.status === "contacted" ? "bg-sky-500/15 text-sky-600 border-sky-500/30" :
                                  r.status === "in-repair" ? "bg-amber-500/15 text-amber-600 border-amber-500/30" :
                                  r.status === "ready" ? "bg-purple-500/15 text-purple-600 border-purple-500/30" :
                                  r.status === "delivered" ? "bg-emerald-500/15 text-emerald-600 border-emerald-500/30" :
                                  "bg-destructive/15 text-destructive border-destructive/30"
                                }>{r.status}</Badge>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new">New</SelectItem>
                                <SelectItem value="contacted">Contacted</SelectItem>
                                <SelectItem value="in-repair">In Repair</SelectItem>
                                <SelectItem value="ready">Ready</SelectItem>
                                <SelectItem value="delivered">Delivered</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-3 text-right">
                            <a href={`https://wa.me/${phone}?text=${waMsg}`} target="_blank" rel="noreferrer" data-testid={`repair-whatsapp-${r.ref}`}>
                              <Button size="sm" className="bg-[#25D366] hover:bg-[#20BD5A] text-white h-8">
                                <MessageCircle className="w-3.5 h-3.5" />
                              </Button>
                            </a>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {editing && (
        <ProductEditor
          product={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadAll(); }}
        />
      )}
      {editingResale && (
        <ResaleEditor
          product={editingResale}
          onClose={() => setEditingResale(null)}
          onSaved={() => { setEditingResale(null); loadAll(); }}
        />
      )}
    </div>
  );
}

function ResaleEditor({ product, onClose, onSaved }) {
  const [form, setForm] = useState({
    brand: product.brand || "apple",
    name: product.name || "",
    storage: product.storage || "",
    condition: product.condition || "excellent",
    price: product.price || 0,
    original_price: product.original_price || 0,
    image_url: product.image_url || "",
    description: product.description || "",
    warranty_months: product.warranty_months ?? 3,
    active: product.active ?? true,
    sold: product.sold ?? false,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);
  const isNew = !!product.new;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("tv_admin_token");
      const res = await fetch(`${API}/upload`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `Upload failed`);
      const data = await res.json();
      setForm((f) => ({ ...f, image_url: `${API.replace(/\/api$/, "")}${data.url}` }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const save = async () => {
    if (!form.name || !form.price || !form.image_url) {
      toast.error("Model name, price and image are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, price: Number(form.price), original_price: Number(form.original_price || 0), warranty_months: Number(form.warranty_months || 3) };
      if (isNew) {
        await api.post("/resale", payload);
        toast.success("Stock added");
      } else {
        await api.put(`/resale/${product.id}`, payload);
        toast.success("Stock updated");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="resale-editor">
        <DialogHeader><DialogTitle>{isNew ? "Add Available Stock" : "Edit Stock Item"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-2">
            <Label>Product Photo *</Label>
            <div className="flex items-center gap-3">
              {form.image_url ? (
                <img src={form.image_url} alt="preview" className="w-24 h-24 rounded-lg object-cover border border-border" />
              ) : (
                <div className="w-24 h-24 rounded-lg border-2 border-dashed border-border flex items-center justify-center text-muted-foreground">
                  <ShoppingBag className="w-8 h-8" />
                </div>
              )}
              <div className="flex-1 space-y-2">
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="resale-image-file" />
                <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="resale-upload-btn">
                  <Upload className="w-3.5 h-3.5 mr-1.5" /> {uploading ? "Uploading…" : "Upload photo"}
                </Button>
                <Input value={form.image_url} onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))} placeholder="Or paste URL…" className="text-xs" data-testid="resale-image-url" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label>Brand *</Label>
              <Select value={form.brand} onValueChange={(v) => setForm((f) => ({ ...f, brand: v }))}>
                <SelectTrigger data-testid="resale-brand"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["apple","samsung","oneplus","xiaomi","vivo","oppo","realme","google","motorola","nothing","honor","asus","sony","tecno","infinix","nokia"].map((b) => (
                    <SelectItem key={b} value={b} className="capitalize">{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Storage / Config</Label>
              <Input value={form.storage} onChange={(e) => setForm((f) => ({ ...f, storage: e.target.value }))} placeholder="e.g. 128 GB" data-testid="resale-storage" />
            </div>
          </div>
          <div className="grid gap-2">
            <Label>Model Name *</Label>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="iPhone 14 Pro" data-testid="resale-name" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="grid gap-2">
              <Label>Condition *</Label>
              <Select value={form.condition} onValueChange={(v) => setForm((f) => ({ ...f, condition: v }))}>
                <SelectTrigger data-testid="resale-condition"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="excellent">Excellent</SelectItem>
                  <SelectItem value="very-good">Very Good</SelectItem>
                  <SelectItem value="good">Good</SelectItem>
                  <SelectItem value="fair">Fair</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Selling Price ₹ *</Label>
              <Input type="number" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} data-testid="resale-price" />
            </div>
            <div className="grid gap-2">
              <Label>MRP ₹ (optional)</Label>
              <Input type="number" value={form.original_price} onChange={(e) => setForm((f) => ({ ...f, original_price: e.target.value }))} data-testid="resale-original-price" />
            </div>
          </div>
          <div className="grid gap-2">
            <Label>Warranty (months)</Label>
            <Input type="number" value={form.warranty_months} onChange={(e) => setForm((f) => ({ ...f, warranty_months: e.target.value }))} data-testid="resale-warranty" />
          </div>
          <div className="grid gap-2">
            <Label>Description (optional)</Label>
            <Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Battery health 92%, comes with original box…" data-testid="resale-description" />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} data-testid="resale-active" />
              Live on site
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.sold} onChange={(e) => setForm((f) => ({ ...f, sold: e.target.checked }))} data-testid="resale-sold" />
              Mark as sold
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-resale-btn">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ Icon, label, value, accent }) {
  const bg = accent === "amber" ? "bg-amber-500/10 text-amber-600" : accent === "emerald" ? "bg-emerald-500/10 text-emerald-600" : "bg-primary/10 text-primary";
  return (
    <Card>
      <CardContent className="p-5 flex items-center gap-4">
        <div className={`w-11 h-11 rounded-xl ${bg} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-widest">{label}</div>
          <div className="text-xl font-bold font-mono">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function ProductEditor({ product, onClose, onSaved }) {
  const [form, setForm] = useState({
    brand: product.brand,
    name: product.name,
    image_url: product.image_url || "",
    active: product.active ?? true,
    storage_variants: product.storage_variants.map((v) => ({ ...v })),
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);
  const isNew = !!product.new;

  const updateVariant = (idx, field, val) => {
    setForm((f) => {
      const copy = [...f.storage_variants];
      copy[idx] = { ...copy[idx], [field]: field === "base_price" ? Number(val) : val };
      return { ...f, storage_variants: copy };
    });
  };
  const addVariant = () => setForm((f) => ({ ...f, storage_variants: [...f.storage_variants, { label: "", base_price: 0 }] }));
  const removeVariant = (idx) => setForm((f) => ({ ...f, storage_variants: f.storage_variants.filter((_, i) => i !== idx) }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("tv_admin_token");
      const res = await fetch(`${API}/upload`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Upload failed (${res.status})`);
      }
      const data = await res.json();
      setForm((f) => ({ ...f, image_url: `${API.replace(/\/api$/, "")}${data.url}` }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, storage_variants: form.storage_variants.filter((v) => v.label && v.base_price > 0) };
      if (isNew) {
        await api.post("/products", payload);
        toast.success("Product created");
      } else {
        await api.put(`/products/${product.id}`, payload);
        toast.success("Product updated");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="product-editor">
        <DialogHeader><DialogTitle>{isNew ? "Add Product" : "Edit Product"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-2">
            <Label>Brand</Label>
            <Select value={form.brand} onValueChange={(v) => setForm((f) => ({ ...f, brand: v }))}>
              <SelectTrigger data-testid="editor-brand"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="apple">Apple</SelectItem>
                <SelectItem value="samsung">Samsung</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>Model Name</Label>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="editor-name" />
          </div>
          <div className="grid gap-2">
            <Label>Product Image</Label>
            <div className="flex items-center gap-3">
              {form.image_url ? (
                <img src={form.image_url} alt="preview" className="w-20 h-20 rounded-lg object-cover border border-border" />
              ) : (
                <div className="w-20 h-20 rounded-lg border border-dashed border-border flex items-center justify-center text-muted-foreground">
                  <Package className="w-6 h-6" />
                </div>
              )}
              <div className="flex-1 space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleUpload}
                  className="hidden"
                  data-testid="editor-image-file"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="editor-upload-btn"
                >
                  <Upload className="w-3.5 h-3.5 mr-1.5" /> {uploading ? "Uploading…" : "Upload image"}
                </Button>
                <Input
                  value={form.image_url}
                  onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))}
                  placeholder="Or paste URL…"
                  className="text-xs"
                  data-testid="editor-image-url"
                />
              </div>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Storage Variants</Label>
              <Button size="sm" variant="outline" onClick={addVariant} data-testid="add-variant-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add
              </Button>
            </div>
            <div className="space-y-2">
              {form.storage_variants.map((v, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={v.label} onChange={(e) => updateVariant(i, "label", e.target.value)} placeholder="128 GB" className="flex-1" data-testid={`variant-label-${i}`} />
                  <Input type="number" value={v.base_price} onChange={(e) => updateVariant(i, "base_price", e.target.value)} placeholder="₹ base" className="w-32" data-testid={`variant-price-${i}`} />
                  <Button variant="ghost" size="sm" onClick={() => removeVariant(i)} className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input id="active" type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} data-testid="editor-active" />
            <Label htmlFor="active">Active (visible on site)</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-product-btn">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
